import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Spinner, Button, Input, Textarea, Switch, Chip, Select, SelectItem,
} from '@nextui-org/react';
import {
  ArrowLeft, Save, MapPin, Plus, Trash2, Upload, Image as ImageIcon,
  Phone, Mail, Link2, X, Calendar, EuroIcon, Hash,
} from 'lucide-react';
import seriesService from '../services/seriesService';
import type { Series, SeriesAddress, SeriesSchedule, DartCloudTime } from '../services/seriesService';

const TEAL = '#3FCED2';

const WEEKDAYS = [
  { value: '0', label: 'Sonntag' },
  { value: '1', label: 'Montag' },
  { value: '2', label: 'Dienstag' },
  { value: '3', label: 'Mittwoch' },
  { value: '4', label: 'Donnerstag' },
  { value: '5', label: 'Freitag' },
  { value: '6', label: 'Samstag' },
];

const SCHEDULE_TYPES = [
  { value: 'weekly', label: 'Wöchentlich' },
  { value: 'biweekly', label: 'Alle 2 Wochen' },
  { value: 'monthly_weekday', label: 'Monatlich (Wochentag)' },
  { value: 'custom', label: 'Individuelle Termine' },
];

const POSITIONS = [
  { value: 'every', label: 'jeden' },
  { value: 'first', label: 'jeden ersten' },
  { value: 'second', label: 'jeden zweiten' },
  { value: 'third', label: 'jeden dritten' },
  { value: 'fourth', label: 'jeden vierten' },
  { value: 'last', label: 'jeden letzten' },
];

const REGISTRATION_TYPES = [
  { value: 'DARTCLOUD', label: 'DartCloud Anmeldung' },
  { value: 'EXTERNAL', label: 'Externer Link' },
  { value: 'NONE', label: 'Keine Anmeldung' },
];

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const timeToString = (t?: DartCloudTime | null): string => {
  if (!t || typeof t.hour !== 'number' || typeof t.minute !== 'number') return '';
  return `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`;
};

const stringToTime = (s: string): DartCloudTime | null => {
  if (!s) return null;
  const [h, m] = s.split(':').map((x) => parseInt(x, 10));
  if (isNaN(h) || isNaN(m)) return null;
  return { hour: h, minute: m };
};

const EditSeriesPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [series, setSeries] = useState<Series | null>(null);
  const [changed, setChanged] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState<SeriesAddress | null>(null);
  const [categories, setCategories] = useState<string[]>([]);

  // Schedule
  const [scheduleType, setScheduleType] = useState<string>('');
  const [scheduleWeekday, setScheduleWeekday] = useState<string>('');
  const [schedulePosition, setSchedulePosition] = useState<string>('');

  // Game
  const [gameMode, setGameMode] = useState('');
  const [boards, setBoards] = useState<string>('');
  const [teamSize, setTeamSize] = useState<string>('');
  const [teamSizeMax, setTeamSizeMax] = useState<string>('');

  // Times
  const [doorsOpen, setDoorsOpen] = useState('');
  const [start, setStart] = useState('');
  const [entryDeadlineOnSite, setEntryDeadlineOnSite] = useState('');

  // Entry
  const [registrationType, setRegistrationType] = useState<string>('DARTCLOUD');
  const [entryEnabled, setEntryEnabled] = useState(false);
  const [entryFee, setEntryFee] = useState<string>('');
  const [entryLimit, setEntryLimit] = useState<string>('');
  const [entryText, setEntryText] = useState('');
  const [entryLink, setEntryLink] = useState('');
  const [showEntryList, setShowEntryList] = useState(true);
  const [showWaitingList, setShowWaitingList] = useState(false);
  const [waitingEnabled, setWaitingEnabled] = useState(false);
  const [leaveEntryAllowed, setLeaveEntryAllowed] = useState(true);
  const [entryBirthdayRequired, setEntryBirthdayRequired] = useState(false);
  const [entryEmailRequired, setEntryEmailRequired] = useState(false);
  const [entryPhoneRequired, setEntryPhoneRequired] = useState(false);

  // Visibility
  const [visible, setVisible] = useState(true);
  const [cancelled, setCancelled] = useState(false);

  const fetchSeries = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await seriesService.getSeriesById(id);
      const s = (res as any)?.data ?? res;
      if (!s) { setMessage({ type: 'error', text: 'Turnierserie konnte nicht geladen werden' }); return; }
      setSeries(s);
      setName(s.name ?? '');
      setEmail(s.email ?? '');
      setPhone(s.phone ?? '');
      setDescription(s.description ?? '');
      setAddress(s.address ?? null);
      setCategories(s.categories ?? []);

      const sch = s.schedule ?? null;
      setScheduleType(sch?.type ?? '');
      setScheduleWeekday(typeof sch?.weekday === 'number' ? String(sch.weekday) : '');
      setSchedulePosition(sch?.weekdayPosition ?? '');

      setGameMode(s.gameMode ?? '');
      setBoards(s.boards != null ? String(s.boards) : '');
      setTeamSize(s.teamSize != null ? String(s.teamSize) : '');
      setTeamSizeMax(s.teamSizeMax != null ? String(s.teamSizeMax) : '');

      setDoorsOpen(timeToString(s.doorsOpen));
      setStart(timeToString(s.start));
      setEntryDeadlineOnSite(timeToString(s.entryDeadlineOnSite));

      setRegistrationType(s.registrationType ?? 'DARTCLOUD');
      setEntryEnabled(s.entryEnabled ?? false);
      setEntryFee(s.entryFee != null ? String(s.entryFee) : '');
      setEntryLimit(s.entryLimit != null ? String(s.entryLimit) : '');
      setEntryText(s.entryText ?? '');
      setEntryLink(s.entryLink ?? '');
      setShowEntryList(s.showEntryList ?? true);
      setShowWaitingList(s.showWaitingList ?? false);
      setWaitingEnabled(s.waitingEnabled ?? false);
      setLeaveEntryAllowed(s.leaveEntryAllowed ?? true);
      setEntryBirthdayRequired(s.entryBirthdayRequired ?? false);
      setEntryEmailRequired(s.entryEmailRequired ?? false);
      setEntryPhoneRequired(s.entryPhoneRequired ?? false);

      setVisible(s.visible ?? true);
      setCancelled(s.cancelled ?? false);

      setChanged(false);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Turnierserie konnte nicht geladen werden' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchSeries(); window.scrollTo({ top: 0 }); }, [fetchSeries]);
  const markChanged = () => setChanged(true);

  const toggleCategory = (cat: string) => {
    setCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
    markChanged();
  };

  const buildSchedule = (): SeriesSchedule | null => {
    if (!scheduleType) return null;
    const sch: SeriesSchedule = { type: scheduleType as any };
    if (scheduleWeekday !== '') {
      const wd = parseInt(scheduleWeekday, 10);
      if (!isNaN(wd)) sch.weekday = wd;
    }
    if (scheduleType === 'monthly_weekday' && schedulePosition) {
      sch.weekdayPosition = schedulePosition as any;
    }
    return sch;
  };

  const handleSave = async () => {
    if (!name.trim()) { setMessage({ type: 'error', text: 'Bitte einen Namen festlegen' }); return; }
    if (!series?._id && !series?.id) return;
    setSaving(true); setMessage(null);
    try {
      const payload: any = {
        id: series._id ?? series.id,
        name,
        email: email.trim() === '' ? null : email,
        phone: phone.trim() === '' ? null : phone,
        description: description.trim() === '' ? null : description,
        categories,
        schedule: buildSchedule(),
        gameMode: gameMode.trim() === '' ? null : gameMode,
        boards: boards === '' ? null : parseInt(boards, 10),
        teamSize: teamSize === '' ? null : parseInt(teamSize, 10),
        teamSizeMax: teamSizeMax === '' ? null : parseInt(teamSizeMax, 10),
        doorsOpen: stringToTime(doorsOpen),
        start: stringToTime(start),
        entryDeadlineOnSite: stringToTime(entryDeadlineOnSite),
        registrationType,
        entryEnabled,
        entryFee: entryFee === '' ? null : parseFloat(entryFee),
        entryLimit: entryLimit === '' ? null : parseInt(entryLimit, 10),
        entryText: entryText.trim() === '' ? null : entryText,
        entryLink: entryLink.trim() === '' ? null : entryLink,
        showEntryList,
        showWaitingList,
        waitingEnabled,
        leaveEntryAllowed,
        entryBirthdayRequired,
        entryEmailRequired,
        entryPhoneRequired,
        visible,
        cancelled,
      };
      await seriesService.updateSeries(payload);
      setChanged(false);
      setMessage({ type: 'success', text: 'Erfolgreich aktualisiert' });
      await fetchSeries();
    } catch (err: any) {
      console.error(err);
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Aktualisierung fehlgeschlagen' });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (changed && !window.confirm('Deine Änderungen wurden noch nicht gespeichert. Wirklich abbrechen?')) return;
    navigate('/verwaltung?tab=series');
  };

  const handlePosterUpload = async (file: File) => {
    if (!series?._id && !series?.id) return;
    if (file.size > 20 * 1024 * 1024) { setMessage({ type: 'error', text: 'Datei zu groß (Max. 20 MB)' }); return; }
    setSaving(true);
    try {
      const base64 = await fileToBase64(file);
      await seriesService.updateSeries({ id: series._id ?? series.id, image: base64 });
      await fetchSeries();
      setMessage({ type: 'success', text: 'Plakat aktualisiert' });
    } catch { setMessage({ type: 'error', text: 'Upload fehlgeschlagen' }); }
    finally { setSaving(false); }
  };

  const handlePosterRemove = async () => {
    if (!series?._id && !series?.id) return;
    if (!window.confirm('Plakat wirklich entfernen?')) return;
    setSaving(true);
    try {
      await seriesService.updateSeries({ id: series._id ?? series.id, image: null });
      await fetchSeries();
      setMessage({ type: 'success', text: 'Plakat entfernt' });
    } catch { setMessage({ type: 'error', text: 'Entfernen fehlgeschlagen' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!series?._id && !series?.id) return;
    if (!window.confirm('Turnierserie wirklich unwiderruflich löschen?')) return;
    setSaving(true);
    try {
      await seriesService.deleteSeries((series._id ?? series.id) as string);
      navigate('/verwaltung?tab=series');
    } catch (err: any) {
      console.error(err);
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Löschen fehlgeschlagen' });
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <Spinner color="primary" size="lg" label="Turnierserie laden..." /></div>;
  }
  if (!series) {
    return <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-4">
      <p className="text-zinc-400 mb-4">Turnierserie konnte nicht geladen werden.</p>
      <Button onPress={handleBack} startContent={<ArrowLeft size={16} />}>Zurück</Button>
    </div>;
  }

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Helmet><title>{name || 'Turnierserie'} bearbeiten - DartCloud</title></Helmet>

      <div className="sticky top-0 z-10 bg-black/80 backdrop-blur border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={handleBack} className="text-zinc-300 hover:text-white p-1"><ArrowLeft size={20} /></button>
          <h1 className="text-lg font-bold flex-1 truncate">Turnierserie bearbeiten</h1>
          {changed && <Chip size="sm" color="warning" variant="flat">Ungespeichert</Chip>}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-8">
        {message && (
          <div className={`rounded-xl p-3 text-sm flex items-start gap-2 ${
            message.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-300' : 'bg-red-500/10 border border-red-500/30 text-red-300'
          }`}>
            <span className="flex-1">{message.text}</span>
            <button onClick={() => setMessage(null)}><X size={14} /></button>
          </div>
        )}

        <section>
          <h2 className="text-base font-semibold mb-2">Name</h2>
          <Input value={name} onValueChange={(v) => { setName(v); markChanged(); }} placeholder="Name"
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Kategorien</h2>
          <div className="flex flex-wrap gap-2">
            {(['steeldart', 'edart', 'online'] as const).map((cat) => {
              const active = categories.includes(cat);
              return (
                <button key={cat} onClick={() => toggleCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition ${
                    active ? 'border-transparent' : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:text-white'
                  }`}
                  style={active ? { backgroundColor: TEAL, color: '#000' } : {}}
                >
                  {cat === 'steeldart' ? 'Steeldart' : cat === 'edart' ? 'E-Dart' : 'Online'}
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Zeitplan</h2>
          <div className="space-y-3">
            <Select
              label="Typ" selectedKeys={scheduleType ? [scheduleType] : []}
              onSelectionChange={(keys) => {
                const v = Array.from(keys)[0] as string;
                setScheduleType(v ?? ''); markChanged();
              }}
              classNames={{ trigger: 'bg-zinc-900 border border-zinc-800' }}
            >
              {SCHEDULE_TYPES.map((t) => <SelectItem key={t.value}>{t.label}</SelectItem>)}
            </Select>

            {scheduleType && scheduleType !== 'custom' && (
              <Select
                label="Wochentag" selectedKeys={scheduleWeekday ? [scheduleWeekday] : []}
                onSelectionChange={(keys) => {
                  const v = Array.from(keys)[0] as string;
                  setScheduleWeekday(v ?? ''); markChanged();
                }}
                classNames={{ trigger: 'bg-zinc-900 border border-zinc-800' }}
              >
                {WEEKDAYS.map((d) => <SelectItem key={d.value}>{d.label}</SelectItem>)}
              </Select>
            )}

            {scheduleType === 'monthly_weekday' && (
              <Select
                label="Position im Monat" selectedKeys={schedulePosition ? [schedulePosition] : []}
                onSelectionChange={(keys) => {
                  const v = Array.from(keys)[0] as string;
                  setSchedulePosition(v ?? ''); markChanged();
                }}
                classNames={{ trigger: 'bg-zinc-900 border border-zinc-800' }}
              >
                {POSITIONS.map((p) => <SelectItem key={p.value}>{p.label}</SelectItem>)}
              </Select>
            )}
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Spielmodus</h2>
          <div className="space-y-3">
            <Input value={gameMode} onValueChange={(v) => { setGameMode(v); markChanged(); }}
              placeholder="z.B. 501 Double Out, Best of 5 Legs"
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input value={boards} onValueChange={(v) => { setBoards(v); markChanged(); }}
                type="number" placeholder="Boards"
                startContent={<Hash size={14} className="text-zinc-400" />}
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
              <Input value={teamSize} onValueChange={(v) => { setTeamSize(v); markChanged(); }}
                type="number" placeholder="Teamgröße"
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
              <Input value={teamSizeMax} onValueChange={(v) => { setTeamSizeMax(v); markChanged(); }}
                type="number" placeholder="Max. Teamgröße"
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Uhrzeiten</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input value={doorsOpen} onValueChange={(v) => { setDoorsOpen(v); markChanged(); }}
              type="time" label="Einlass"
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            <Input value={start} onValueChange={(v) => { setStart(v); markChanged(); }}
              type="time" label="Start"
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            <Input value={entryDeadlineOnSite}
              onValueChange={(v) => { setEntryDeadlineOnSite(v); markChanged(); }}
              type="time" label="Anmeldeschluss vor Ort"
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Anmeldung</h2>
          <div className="space-y-3">
            <Select
              label="Anmeldetyp" selectedKeys={[registrationType]}
              onSelectionChange={(keys) => {
                const v = Array.from(keys)[0] as string;
                if (v) { setRegistrationType(v); markChanged(); }
              }}
              classNames={{ trigger: 'bg-zinc-900 border border-zinc-800' }}
            >
              {REGISTRATION_TYPES.map((t) => <SelectItem key={t.value}>{t.label}</SelectItem>)}
            </Select>

            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">Anmeldung aktiv</span>
              <Switch isSelected={entryEnabled} onValueChange={(v) => { setEntryEnabled(v); markChanged(); }} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={entryFee} onValueChange={(v) => { setEntryFee(v); markChanged(); }}
                type="number" step="0.01" label="Startgebühr (€)"
                startContent={<EuroIcon size={14} className="text-zinc-400" />}
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
              <Input value={entryLimit} onValueChange={(v) => { setEntryLimit(v); markChanged(); }}
                type="number" label="Anmeldelimit"
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            </div>

            {registrationType === 'EXTERNAL' && (
              <Input value={entryLink} onValueChange={(v) => { setEntryLink(v); markChanged(); }}
                label="Externer Anmeldelink" placeholder="https://..."
                startContent={<Link2 size={14} className="text-zinc-400" />}
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            )}

            <Textarea value={entryText} onValueChange={(v) => { setEntryText(v); markChanged(); }}
              label="Hinweis zur Anmeldung" minRows={2}
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { label: 'Teilnehmerliste anzeigen', val: showEntryList, set: setShowEntryList },
                { label: 'Warteliste anzeigen', val: showWaitingList, set: setShowWaitingList },
                { label: 'Warteliste aktiv', val: waitingEnabled, set: setWaitingEnabled },
                { label: 'Abmeldung erlauben', val: leaveEntryAllowed, set: setLeaveEntryAllowed },
                { label: 'Geburtstag erforderlich', val: entryBirthdayRequired, set: setEntryBirthdayRequired },
                { label: 'E-Mail erforderlich', val: entryEmailRequired, set: setEntryEmailRequired },
                { label: 'Telefon erforderlich', val: entryPhoneRequired, set: setEntryPhoneRequired },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
                  <span className="text-sm">{row.label}</span>
                  <Switch isSelected={row.val} onValueChange={(v) => { row.set(v); markChanged(); }} />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Kontakt</h2>
          <div className="space-y-3">
            <Input value={email} onValueChange={(v) => { setEmail(v); markChanged(); }} placeholder="E-Mail" type="email"
              startContent={<Mail size={16} className="text-zinc-400" />}
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            <Input value={phone} onValueChange={(v) => { setPhone(v); markChanged(); }} placeholder="Telefon"
              startContent={<Phone size={16} className="text-zinc-400" />}
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Adresse</h2>
          {address ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3 flex items-center gap-3">
              <MapPin size={18} className="text-zinc-400 flex-shrink-0" />
              <span className="flex-1 text-sm truncate">{address.label || 'Keine Adresse'}</span>
              <Button isIconOnly size="sm" variant="light" color="danger"
                onPress={() => { setAddress(null); markChanged(); }}><Trash2 size={14} /></Button>
            </div>
          ) : (
            <p className="text-sm text-zinc-400">Keine Adresse hinterlegt. Adressen können aktuell nur in der Mobile-App geändert werden.</p>
          )}
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Beschreibung</h2>
          <Textarea value={description} onValueChange={(v) => { setDescription(v); markChanged(); }}
            placeholder="Beschreibung der Turnierserie" minRows={6}
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
        </section>

        <section>
          <h2 className="text-base font-semibold mb-1">Plakat</h2>
          {series.image && (
            <div className="mb-3">
              <div className="h-48 bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
                <img src={seriesService.buildFileUrl(series.image) ?? ''} alt="Plakat" className="w-full h-full object-contain" />
              </div>
              <Button size="sm" variant="light" color="danger" className="mt-2" onPress={handlePosterRemove}
                startContent={<Trash2 size={14} />}>Plakat entfernen</Button>
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 rounded-xl cursor-pointer text-sm">
            <Upload size={14} /> Plakat hochladen
            <input type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePosterUpload(f); e.target.value = ''; }} />
          </label>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Sichtbarkeit</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">Öffentlich sichtbar</span>
              <Switch isSelected={visible} onValueChange={(v) => { setVisible(v); markChanged(); }} />
            </div>
            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">Serie abgesagt</span>
              <Switch isSelected={cancelled} onValueChange={(v) => { setCancelled(v); markChanged(); }} />
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2 flex items-center gap-2">
            <Calendar size={16} /> Termine
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-sm text-zinc-400 flex items-center gap-2">
            <ImageIcon size={16} className="flex-shrink-0" />
            <span>Die Verwaltung einzelner Termine erfolgt aktuell in der Mobile-App.</span>
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2 text-red-400">Gefahrenzone</h2>
          <Button color="danger" variant="bordered" startContent={<Trash2 size={16} />} onPress={handleDelete}>
            Turnierserie löschen
          </Button>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur border-t border-zinc-800 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
          <Button className="w-full" isDisabled={!changed || saving} isLoading={saving} onPress={handleSave}
            startContent={!saving ? <Save size={16} /> : null}
            style={{ backgroundColor: TEAL, color: '#000' }}>Aktualisieren</Button>
        </div>
      </div>
    </div>
  );
};

export default EditSeriesPage;
